import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonBox from "components/ArgonBox";
import ChipInput from "./ChipInput";

const ProposalPlanDialog = ({ open, onClose, proposal, onSave, loading, isEdit }) => {
  const [formData, setFormData] = useState({
    duration: "",
    months: "",
    discountPercentage: "",
    title: "",
    description: "",
    isRecommended: false,
    isPremium: false,
    benefits: [],
  });

  useEffect(() => {
    if (proposal && isEdit) {
      setFormData({
        duration: proposal.duration || "",
        months: proposal.months || "",
        discountPercentage: proposal.discountPercentage || "",
        title: proposal.title || "",
        description: proposal.description || "",
        isRecommended: proposal.isRecommended || false,
        isPremium: proposal.isPremium || false,
        benefits: proposal.benefits || [],
      });
    } else {
      setFormData({
        duration: "",
        months: "",
        discountPercentage: "",
        title: "",
        description: "",
        isRecommended: false,
        isPremium: false,
        benefits: [],
      });
    }
  }, [proposal, isEdit, open]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBenefitsChange = (newBenefits) => {
    setFormData((prev) => ({
      ...prev,
      benefits: newBenefits,
    }));
  };

  const handleAddBenefit = (benefit) => {
    setFormData((prev) => ({
      ...prev,
      benefits: [...prev.benefits, benefit],
    }));
  };

  const handleDeleteBenefit = (index) => {
    setFormData((prev) => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "Edit Proposal Plan" : "Create Proposal Plan"}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <ArgonBox display="flex" flexDirection="column" gap={3}>
            <ArgonBox display="flex" gap={2}>
              <ArgonBox flex={1}>
                <Typography variant="body2" fontWeight="medium" mb={1}>
                  Duration *
                </Typography>
                <ArgonInput
                  placeholder="e.g., 3 Months"
                  value={formData.duration}
                  onChange={handleChange("duration")}
                  required
                  fullWidth
                />
              </ArgonBox>
              <ArgonBox flex={1}>
                <Typography variant="body2" fontWeight="medium" mb={1}>
                  Months *
                </Typography>
                <ArgonInput
                  type="number"
                  placeholder="e.g., 3"
                  value={formData.months}
                  onChange={handleChange("months")}
                  required
                  fullWidth
                />
              </ArgonBox>
            </ArgonBox>

            <ArgonBox display="flex" gap={2}>
              <ArgonBox flex={1}>
                <Typography variant="body2" fontWeight="medium" mb={1}>
                  Discount Percentage *
                </Typography>
                <ArgonInput
                  type="number"
                  placeholder="e.g., 10"
                  value={formData.discountPercentage}
                  onChange={handleChange("discountPercentage")}
                  required
                  fullWidth
                />
              </ArgonBox>
              <ArgonBox flex={1}>
                <Typography variant="body2" fontWeight="medium" mb={1}>
                  Title *
                </Typography>
                <ArgonInput
                  placeholder="e.g., Short-term goals"
                  value={formData.title}
                  onChange={handleChange("title")}
                  required
                  fullWidth
                />
              </ArgonBox>
            </ArgonBox>

            <ArgonBox>
              <Typography variant="body2" fontWeight="medium" mb={1}>
                Description *
              </Typography>
              <ArgonInput
                placeholder="Description of the proposal plan"
                value={formData.description}
                onChange={handleChange("description")}
                multiline
                rows={3}
                required
                fullWidth
              />
            </ArgonBox>

            <ArgonBox display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRecommended}
                    onChange={handleChange("isRecommended")}
                    color="primary"
                    sx={{ marginLeft: 2 }}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="medium" ml={2}>
                    Recommended Plan
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPremium}
                    onChange={handleChange("isPremium")}
                    color="warning"
                    sx={{ marginLeft: 2 }}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="medium" ml={2}>
                    Premium Plan
                  </Typography>
                }
              />
            </ArgonBox>

            <ArgonBox>
              <Typography variant="body2" fontWeight="medium" mb={1}>
                Benefits
              </Typography>
              <ChipInput
                items={formData.benefits}
                onAdd={handleAddBenefit}
                onDelete={handleDeleteBenefit}
                placeholder="Add benefit and press Enter"
              />
            </ArgonBox>
          </ArgonBox>
        </DialogContent>
        <DialogActions>
          <ArgonButton onClick={onClose} variant="outlined" color="secondary">
            Cancel
          </ArgonButton>
          <ArgonButton type="submit" variant="contained" color="info" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update" : "Create"}
          </ArgonButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

ProposalPlanDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  proposal: PropTypes.shape({
    duration: PropTypes.string,
    months: PropTypes.number,
    discountPercentage: PropTypes.number,
    title: PropTypes.string,
    description: PropTypes.string,
    isRecommended: PropTypes.bool,
    isPremium: PropTypes.bool,
    benefits: PropTypes.arrayOf(PropTypes.string),
  }),
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
};

export default ProposalPlanDialog;
